import { create } from "zustand";
import { CommandingRunController } from "../controllers/commandingRunController";
import { nativeBridge } from "../embed/nativeBridge";
import { BRIDGE_RUN_UPDATE_EVENT } from "../embed/nativeBridgeMessages";
import {
  CommandingRunProgressReply,
  ConfirmRunAssetReply,
  EngineRunUpdate,
  PrepareCommandingRunReply,
} from "@/types/commandingRunTypes";
import { RUN_STATE } from "@/types/commandingRunTaskTypes";
import { CommandingRunTask } from "@/backend/models/commandingRunTask";
import { cleanUpError } from "@/errors/cleanUpError";
import { I18n } from "@/i18n/I18n";
import { resolvePersistedTaskAlias } from "../components/operations/resolveTaskAlias";
import { FusaToastType } from "../toasts/types";
import { useTreeStore } from "./TreeStore";
import { useParameterStore } from "./ParameterStore";
import { useEquipmentStore } from "./EquipmentStore";
import { useGlobalStore } from "./GlobalStore";

/**
 * Runs a commanding's automation.
 *
 * Three processes are involved: this app owns the record of the run, the
 * desktop app drives it, and the workflow engine on the user's machine does the
 * work. A run is
 *
 *   prepare here -> engine.startRun over the bridge -> desktop polls the engine
 *   and PUTs each read to /progress -> the reply comes back here as an event
 *
 * Desktop does the saving, not this page, so a run carries on being recorded
 * when the page is left or reloaded. This page only shows it: it applies each
 * saved reply to the tree and parameter stores, and a page that loads mid-run
 * asks desktop which runs are still going (`reconnect`).
 */

/**
 * What became of one file the run wrote.
 *
 * The engine writes them to the desktop machine, which this page cannot read,
 * so desktop uploads each to S3 and confirms it. `updates` is that confirm's
 * reply: what creating the file changed here.
 */
interface RunAssetResult {
  id: string;
  uploaded: boolean;
  updates?: ConfirmRunAssetReply;
  error?: string;
}

/** What veriform-desktop pushes once it has saved a read. See webRunTypes.ts there. */
interface RunUpdateMessage {
  commandingRunId: string;
  operationId: string;
  /** The last message for the run, whether it finished or failed. */
  done: boolean;
  update?: EngineRunUpdate;
  progress?: CommandingRunProgressReply;
  /** Present when this read reported files, and sent once they are dealt with. */
  assets?: RunAssetResult[];
  error?: string;
}

/** One entry of `engine.activeRuns`. */
interface ActiveRun {
  commandingRunId: string;
  operationId: string;
  processId: string;
}

/**
 * A run in flight. Present from the click; `commandingRunId` once it is
 * prepared, `started` once desktop has it going -- which is when Stop has
 * something to stop -- and `stopping` between asking and the run settling.
 */
interface RunningEntry {
  commandingRunId?: string;
  started?: boolean;
  stopping?: boolean;
}

export interface CommandingRunStore {
  /** Keyed by operation, so a row can ask about its own automation. */
  runningByOperationId: Record<string, RunningEntry>;
  runErrorByOperationId: Record<string, string>;

  runCommanding: (operationId: string) => Promise<void>;
  stopRun: (operationId: string) => Promise<void>;
  /** Picks up the runs desktop is still driving. Once per page load. */
  reconnect: () => Promise<void>;
  reset: () => void;
}

const initialState = {
  runningByOperationId: {} as Record<string, RunningEntry>,
  runErrorByOperationId: {} as Record<string, string>,
};

let unsubscribe: (() => void) | null = null;
let reconnected = false;

export const useCommandingRunStore = create<CommandingRunStore>((set, get) => {
  /**
   * Ends the operation's run in this page: clears it, and sets or clears its
   * error. Only when the run in the entry is the one given, so a message or a
   * reply about an older run cannot touch the one the user started since.
   */
  const end = (operationId: string, commandingRunId: string | undefined, error?: string) => {
    set(state => {
      const entry = state.runningByOperationId[operationId];
      if (!entry || entry.commandingRunId !== commandingRunId) return {};
      const running = { ...state.runningByOperationId };
      delete running[operationId];
      return {
        runningByOperationId: running,
        runErrorByOperationId: { ...state.runErrorByOperationId, [operationId]: error ?? '' },
      };
    });
  };

  const onRunUpdate = (message: RunUpdateMessage) => {
    if (!message?.commandingRunId || !message.operationId) return;
    const { operationId, commandingRunId, progress, update } = message;

    // Only for the procedure on screen. Desktop sends every run it drives, and
    // another procedure's runs have no place in this tree.
    if (progress && useTreeStore.getState().operations.some(operation => operation.id === operationId)) {
      useTreeStore.getState().upsertCommandingRun(progress.commandingRun, progress.tasks);
      // An output task writes a parameter and cascades to whatever consumes it.
      // The reply carries both, in the same `UpdatedParamsAndRefs` shape every
      // other write in this app returns -- without this the task rows go green
      // while the value they produced stays stale until a reload.
      useParameterStore.getState().handleUpdatedParamsAndRefs(progress);
      // After the progress above, not before: that replaces the run's tasks
      // wholesale with rows read before these files existed, which would undo
      // the asset each one just attached.
      applyAssets(message.assets);
    }

    if (!message.done) return;
    if (get().runningByOperationId[operationId]?.commandingRunId !== commandingRunId) return;
    const failure = message.error ?? (update && progress ? reportFailure(update, progress.tasks) : undefined);
    end(operationId, commandingRunId, failure);
  };

  /** One subscription for the page, for every run; messages say whose they are. */
  const listen = () => {
    if (unsubscribe) return;
    unsubscribe = nativeBridge.on(BRIDGE_RUN_UPDATE_EVENT, payload => onRunUpdate(payload as RunUpdateMessage));
  };

  return {
    ...initialState,

    runCommanding: async (operationId: string) => {
      if (get().runningByOperationId[operationId]) return;
      listen();

      set(state => ({
        runningByOperationId: { ...state.runningByOperationId, [operationId]: {} },
        runErrorByOperationId: { ...state.runErrorByOperationId, [operationId]: '' },
      }));

      let prepared: PrepareCommandingRunReply;
      try {
        prepared = await new CommandingRunController().prepare(operationId);
      } catch (error) {
        // `Client` throws, rather than returning an error, when it has no token
        // to build headers with.
        end(operationId, undefined, (error as Error).message);
        return;
      }
      if (prepared.status !== 201) {
        end(operationId, undefined, prepared.errors?.[0]?.message || '');
        return;
      }

      const commandingRunId = prepared.commandingRun.id;
      useTreeStore.getState().upsertCommandingRun(prepared.commandingRun, prepared.tasks);
      set(state => ({
        runningByOperationId: { ...state.runningByOperationId, [operationId]: { commandingRunId } },
      }));

      try {
        await nativeBridge.call('engine.startRun', { operationId, engineRequest: prepared.engineRequest });
        // A run short enough to have finished while the call was in flight has
        // already ended; marking it started would strand it.
        set(state => {
          const entry = state.runningByOperationId[operationId];
          if (entry?.commandingRunId !== commandingRunId) return {};
          return { runningByOperationId: { ...state.runningByOperationId, [operationId]: { ...entry, started: true } } };
        });
      } catch (error) {
        end(operationId, commandingRunId, (error as Error).message);
      }
    },

    stopRun: async (operationId: string) => {
      const entry = get().runningByOperationId[operationId];
      if (!entry?.started || entry.stopping) return;
      const { commandingRunId } = entry;

      set(state => ({
        runningByOperationId: { ...state.runningByOperationId, [operationId]: { ...entry, stopping: true } },
      }));

      try {
        await nativeBridge.call('engine.stopRun', { commandingRunId });
        // Not ended here. Asking the engine to stop is not the same as it having
        // stopped: desktop keeps saving reads until every task has settled, and
        // that message ends the run the same way a finished run's does.
      } catch (error) {
        // Only the stop failed -- most often by timing out, since the engine's
        // Stop blocks on a running task whose provider does not take the cancel
        // command, which is every one but `veriform`. The run is still desktop's
        // and still being saved, so it stays running here, with Stop offered
        // again and the reason beneath it.
        set(state => {
          const current = state.runningByOperationId[operationId];
          if (current?.commandingRunId !== commandingRunId) return {};
          return {
            runningByOperationId: { ...state.runningByOperationId, [operationId]: { ...current, stopping: false } },
            runErrorByOperationId: { ...state.runErrorByOperationId, [operationId]: (error as Error).message },
          };
        });
      }
    },

    reconnect: async () => {
      if (reconnected || !nativeBridge.isAvailable()) return;
      reconnected = true;
      listen();

      let runs: ActiveRun[];
      try {
        runs = await nativeBridge.call<ActiveRun[]>('engine.activeRuns');
      } catch {
        // Nothing to pick up is the same as not being able to ask: the runs
        // themselves are unaffected, only whether this page shows them going.
        return;
      }

      set(state => {
        const running = { ...state.runningByOperationId };
        runs.forEach(({ operationId, commandingRunId }) => {
          running[operationId] ??= { commandingRunId, started: true };
        });
        return { runningByOperationId: running };
      });
    },

    reset: () => {
      unsubscribe?.();
      unsubscribe = null;
      reconnected = false;
      set({ ...initialState });
    },
  };
});

/**
 * Shows the files desktop has just uploaded, without waiting for a reload.
 *
 * A parameter file output reports itself and whatever consumes it, the same way
 * a text output does. A task's own asset consumes nothing, so the confirm
 * returns the task instead and the row is replaced -- the run view renders
 * `task.asset`, and the row this message carried was read before the file
 * existed.
 *
 * A file that could not be sent is said so, once, rather than left to look like
 * a run that produced nothing. The run itself is unaffected: it completed
 * whether or not its output reached S3, and veriform-web keeps the pending row.
 */
function applyAssets(assets: RunAssetResult[] | undefined): void {
  assets?.forEach(asset => {
    if (!asset.uploaded) {
      useGlobalStore.getState().addToast({
        header: I18n.get('error'),
        message: I18n.get('runFileNotSaved'),
        details: asset.error || undefined,
        type: FusaToastType.ERROR,
      });
      return;
    }

    if (!asset.updates) return;
    useParameterStore.getState().handleUpdatedParamsAndRefs(asset.updates);
    if (asset.updates.task) useTreeStore.getState().upsertRunTask(asset.updates.task);
  });
}

/**
 * What a failed run tells the user, from the read that settled it. Returns the
 * run's error, or undefined for a run that did not fail.
 *
 * Desktop's native runner marks the operation errored when a task fails
 * (commandingRunner.ts:152-155). The run error is this app's counterpart, and
 * names the task -- otherwise a failed run ends looking like a clean one,
 * except for its row. The message is the one the server stored against the
 * task, which has already been through `cleanUpError`.
 *
 * Rows are matched by stage and array index, as the server matches them.
 */
function reportFailure(update: EngineRunUpdate, tasks: CommandingRunTask[]): string | undefined {
  for (const stage of update.stagesList ?? []) {
    const engineTasks = stage.taskObjectsList ?? [];
    const index = engineTasks.findIndex(task => task.state === RUN_STATE.ERROR);
    if (index === -1) continue;

    const engineTask = engineTasks[index];
    const row = tasks.find(task => task.stage === stage.name && task.taskOrder === index);
    const alias = resolvePersistedTaskAlias(row?.alias || engineTask.name, useEquipmentStore.getState().equipmentList);

    toastTaskError(alias, engineTask.taskMessage ?? '');
    return I18n.get('taskFailed', { alias, error: row?.statusMessage ?? cleanUpError(engineTask.taskMessage ?? '').error });
  }

  return update.state === RUN_STATE.ERROR ? I18n.get('automationFailed') : undefined;
}

/**
 * The toasts desktop's `handleStatusMessage` raises for a failed task
 * (commandingRunner.ts:222-235), on the same two conditions.
 *
 * A provider error carries a stack trace, which `cleanUpError` splits off. It
 * is not stored -- desktop does not store it either -- so this toast is the
 * only place it is shown. Desktop also asks its instrument list to refresh
 * after a connection failure; that list is desktop's own and has nothing to
 * refresh here.
 */
function toastTaskError(alias: string, taskMessage: string): void {
  const { addToast } = useGlobalStore.getState();

  if (taskMessage.includes('Provider responded with an error code:')) {
    const { error, stackTrace } = cleanUpError(taskMessage);
    addToast({
      header: I18n.get('error'),
      message: I18n.get('taskFailed', { alias, error }),
      details: stackTrace || undefined,
      type: FusaToastType.ERROR,
    });
  }

  if (taskMessage.includes('Unable to connect to instrument')) {
    addToast({
      header: I18n.get('error'),
      message: I18n.get('taskFailed', { alias, error: I18n.get('unableToConnectToInstrument') }),
      type: FusaToastType.ERROR,
    });
  }
}
